"use client";

import { useCallback, useEffect, useState } from "react";
import { useCheckoutQuote } from "@/hooks/use-market-quote";
import { domesticMarket, findMarket, previewFromQuote } from "@/lib/markets";
import { useRouter } from "next/navigation";
import {
  useCheckoutSession,
  CheckoutError as CheckoutErrorClass,
} from "@/hooks/use-checkout";
import { OrderSummary } from "./order-summary";
import { PaymentForm } from "./payment-form";
import { CheckoutError } from "./checkout-error";
import { BrandTheme } from "@/components/brand-theme";
import { CheckoutLayout } from "../../_components/checkout-layout";
import Header from "./header";
import { Spinner } from "@/components/ui/spinner";
import type { Dictionary } from "../../../dictionaries";
import type { CheckoutSession } from "@/lib/api/checkout.schema";

interface CheckoutContentProps {
  token: string;
  lang: string;
  dictionary: Dictionary;
  /** Session already fetched on the server; skips the client round trip. */
  initialSession?: CheckoutSession;
}

type ErrorState =
  | { type: "redirect"; to: "success" }
  | { type: "error"; title: string; message: string };

function getErrorState(
  error: Error | null,
  dictionary: Dictionary,
): ErrorState {
  const { errors } = dictionary;

  if (!error) {
    return {
      type: "error",
      title: errors.generic.title,
      message: errors.generic.description,
    };
  }

  if (error instanceof CheckoutErrorClass) {
    const { statusCode, errorMessage } = error;
    const lowerMessage = errorMessage.toLowerCase();

    // 404 - Session not found
    if (statusCode === 404) {
      return {
        type: "error",
        title: errors.notFound.title,
        message: errors.notFound.description,
      };
    }

    // 409 - Payment already in progress
    if (statusCode === 409) {
      return {
        type: "error",
        title: errors.inProgress.title,
        message: errors.inProgress.description,
      };
    }

    // 410 - Session is gone (completed, cancelled, or expired)
    if (statusCode === 410) {
      if (lowerMessage.includes("completed")) {
        return { type: "redirect", to: "success" };
      }
      if (lowerMessage.includes("expired")) {
        return {
          type: "error",
          title: errors.sessionExpired.title,
          message: errors.sessionExpired.description,
        };
      }
      // Default 410 message
      return {
        type: "error",
        title: errors.sessionUnavailable.title,
        message: errors.sessionUnavailable.description,
      };
    }
  }

  // Default error
  return {
    type: "error",
    title: errors.generic.title,
    message: error.message || errors.generic.description,
  };
}

export function CheckoutContent({
  token,
  lang,
  dictionary,
  initialSession,
}: CheckoutContentProps) {
  const router = useRouter();
  const { data: session, isLoading, error } = useCheckoutSession(
    token,
    initialSession,
  );

  // Shared custom amount state - must be declared before any early returns.
  // Until the customer edits it, fall back to the session's suggested amount.
  const [customAmountOverride, setCustomAmount] = useState<
    number | undefined
  >(undefined);
  const customAmount =
    customAmountOverride ??
    (session?.allow_custom_amount && session.amount > 0
      ? session.amount
      : undefined);

  // ---- Market preview (indicative; the checkout response is binding) ----
  // Follows the payer's phone number once they type one.
  const [previewCountry, setPreviewCountry] = useState<string | null>(null);
  const domestic = session
    ? domesticMarket(session.markets, session.currency)
    : null;
  const previewMarket =
    findMarket(session?.markets, previewCountry) ?? domestic;
  const isForeignPreview =
    !!previewMarket && !!domestic && previewMarket.country !== domestic.country;
  const previewQuote = useCheckoutQuote(
    token,
    {
      country: previewMarket?.country,
      amount: session?.allow_custom_amount ? customAmount : session?.amount,
    },
    isForeignPreview,
  );
  const preview = isForeignPreview ? previewFromQuote(previewQuote.data) : null;

  const handlePayerCountryChange = useCallback(
    (country: string | null) => {
      if (country && findMarket(session?.markets, country)) {
        setPreviewCountry(country);
      }
    },
    [session?.markets],
  );

  // Get error state for redirect handling
  const errorState = error ? getErrorState(error, dictionary) : null;

  // Redirect to correct locale if session specifies a different one
  useEffect(() => {
    if (!session?.locale) return;

    const sessionLocale = session.locale;
    if (
      sessionLocale !== lang &&
      (sessionLocale === "en" || sessionLocale === "sw")
    ) {
      router.replace(`/${sessionLocale}/checkout/${token}`);
    }
  }, [session?.locale, lang, token, router]);

  // Redirect based on session status or error state
  useEffect(() => {
    // Handle redirects from error state (410 completed/cancelled)
    if (errorState?.type === "redirect") {
      router.replace(`/${lang}/checkout/${token}/${errorState.to}`);
      return;
    }

    if (!session) return;

    if (session.status === "completed") {
      router.replace(`/${lang}/checkout/${token}/success`);
    }
    // expired status is handled in PaymentForm
  }, [session, token, lang, router, errorState]);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Spinner className="size-8" />
      </div>
    );
  }

  // Show loading while redirecting for completed/cancelled from error
  if (errorState?.type === "redirect") {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Spinner className="size-8" />
      </div>
    );
  }

  if (error || !session) {
    const state = getErrorState(error, dictionary);
    if (state.type === "error") {
      return <CheckoutError title={state.title} message={state.message} />;
    }
    // Fallback (shouldn't reach here)
    return (
      <CheckoutError
        title={dictionary.errors.generic.title}
        message={dictionary.errors.generic.description}
      />
    );
  }

  // Show loading while redirecting for completed
  if (session.status === "completed") {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Spinner className="size-8" />
      </div>
    );
  }

  const isCustomAmount = session.allow_custom_amount === true;

  const merchantName = session.merchant_name || "Merchant";

  return (
    <BrandTheme brandColor={session.brand_color}>
      <CheckoutLayout
        header={<Header name={merchantName} logo={session.merchant_logo ?? undefined} />}
        summary={
          <OrderSummary
            session={session}
            dictionary={dictionary}
            customAmount={customAmount}
            onAmountChange={isCustomAmount ? setCustomAmount : undefined}
            previewMarket={previewMarket}
            onPreviewMarketChange={setPreviewCountry}
            preview={preview}
          />
        }
      >
        <PaymentForm
          token={token}
          lang={lang}
          session={session}
          dictionary={dictionary}
          customAmount={customAmount}
          preview={preview}
          onPayerCountryChange={handlePayerCountryChange}
        />
      </CheckoutLayout>
    </BrandTheme>
  );
}
